import { generateRandomId } from "@/utils/validators/helpers";
import type {
	EAnaliseEstoqueGroupKey,
	GroupValue,
	IInventoryAnalysisPayload,
	ITopProdutosPayload,
	IFaixaPreco,
	IVitrineAgrupamentoFaixaPreco,
	IVitrineAgrupamentoFiltros,
	IVitrineAgrupamentoResponse,
	IVitrineFaixa,
} from "../../dtos/compra-inteligente.dto";

// ----------------------------------------------- VITRINE DE AGRUPAMENTO ---------------------------------------
export const VITRINE_FAIXA_PRECO_VALOR_PADRAO = 100;

export const LIMITE_ITENS_PDF_VITRINE = 500;

export type FiltrosVitrineAgrupamentoEntrada = Partial<
	Record<EAnaliseEstoqueGroupKey, GroupValue | GroupValue[] | undefined>
> & {
	faixa_preco?: IVitrineAgrupamentoFaixaPreco | null;
};

type IVitrineFaixaEntrada = Omit<IVitrineFaixa, "faixa_preco"> & {
	faixa_preco: Omit<IFaixaPreco, "id"> & { id: IVitrineAgrupamentoFaixaPreco };
};

type IVitrineRespostaEntrada = Omit<Partial<IVitrineAgrupamentoResponse>, "faixas"> & {
	faixas?: readonly IVitrineFaixaEntrada[];
};

function normalizarFaixaPrecoValor(valor: unknown): string | null {
	if (valor === null || valor === undefined) return null;

	if (typeof valor === "number") {
		if (!Number.isFinite(valor)) return null;
		return String(valor);
	}

	if (typeof valor === "string") {
		const valorNormalizado = valor.trim();
		return valorNormalizado || null;
	}

	return null;
}

function normalizarFiltroFaixaPreco(
	faixaPreco?: FiltrosVitrineAgrupamentoEntrada["faixa_preco"],
): IVitrineAgrupamentoFiltros["faixa_preco"] | undefined {
	if (!faixaPreco) return undefined;

	const inicio = normalizarFaixaPrecoValor(faixaPreco.inicio);
	const fim = normalizarFaixaPrecoValor(faixaPreco.fim);
	const ultimaFaixa = faixaPreco.ultima_faixa;
	if (!inicio || !fim) return undefined;

	return {
		inicio,
		fim,
		ultima_faixa: typeof ultimaFaixa === "boolean" ? ultimaFaixa : false,
	};
}

function normalizarFaixasVitrine(faixas: readonly IVitrineFaixaEntrada[] | undefined): IVitrineFaixa[] {
	if (!faixas?.length) return [];

	return faixas.flatMap((faixa) => {
		const id = normalizarFiltroFaixaPreco(faixa.faixa_preco?.id);
		if (!id) return [];

		return [
			{
				faixa_preco: {
					chave: "faixa_preco" as const,
					id,
					descricao: String(faixa.faixa_preco?.descricao ?? "").trim(),
				},
				total_itens: Number(faixa.total_itens) || 0,
			},
		];
	});
}

export function normalizarRespostaVitrine(data: IVitrineRespostaEntrada = {}): IVitrineAgrupamentoResponse {
	return {
		organizacao: data.organizacao ?? "AGRUPAMENTO",
		grupos: data.grupos ?? [],
		faixas: normalizarFaixasVitrine(data.faixas),
		itens: data.itens ?? [],
		cursor_proximo: data.cursor_proximo ?? null,
		has_next: data.has_next ?? false,
	};
}

export function normalizarFaixaPrecoValorVitrine(valor: unknown): number {
	let valorTexto = "";

	if (typeof valor === "number") valorTexto = String(valor);
	else if (typeof valor === "string") valorTexto = valor;
	else if (valor !== null && valor !== undefined) return VITRINE_FAIXA_PRECO_VALOR_PADRAO;

	valorTexto = valorTexto.trim().replace(",", ".");
	const valorNumerico = Number(valorTexto);

	if (!Number.isFinite(valorNumerico) || valorNumerico <= 0) return VITRINE_FAIXA_PRECO_VALOR_PADRAO;

	return valorNumerico;
}

export function normalizarFiltrosVitrineAgrupamento(
	filtros?: FiltrosVitrineAgrupamentoEntrada | null,
): IVitrineAgrupamentoFiltros | undefined {
	if (!filtros) return undefined;

	const filtrosNormalizados: IVitrineAgrupamentoFiltros = {};
	const filtroFaixaPreco = normalizarFiltroFaixaPreco(filtros.faixa_preco);
	if (filtroFaixaPreco) filtrosNormalizados.faixa_preco = filtroFaixaPreco;

	for (const [chave, valor] of Object.entries(filtros)) {
		if (chave === "faixa_preco") continue;

		const valores = (Array.isArray(valor) ? valor : [valor]).filter(
			(item): item is GroupValue => item !== undefined && (item === null || typeof item !== "object"),
		);

		if (valores.length) filtrosNormalizados[chave as EAnaliseEstoqueGroupKey] = valores;
	}

	return Object.keys(filtrosNormalizados).length ? filtrosNormalizados : undefined;
}

// ----------------------------------------------- ANALISE DE ESTOQUE -----------------------------------------------
export function removerConfiguracaoTopProdutos(
	payload: IInventoryAnalysisPayload | ITopProdutosPayload,
): IInventoryAnalysisPayload {
	const payloadBase: Partial<ITopProdutosPayload> = { ...payload };
	delete payloadBase.agrupar_por;
	delete payloadBase.ordenar_por;
	delete payloadBase.ordem;
	delete payloadBase.limite;

	return payloadBase as IInventoryAnalysisPayload;
}

export function reutilizaRelatorioExistente(
	payload: IInventoryAnalysisPayload | ITopProdutosPayload | null | undefined,
): boolean {
	return payload?.gerar_dados === false;
}

/**
 * Garante que o filtro fornecido contenha `report_id` se fornecido.
 * Retorna um novo objeto ou undefined se o filtro for undefined.
 */
export function withReportId(
	filter: Record<string, unknown> | undefined,
	reportId?: string,
): Record<string, unknown> | undefined {
	if (!filter && !reportId) return undefined;
	const result: Record<string, unknown> = filter ? { ...filter } : {};

	if (reportId) result.report_id = reportId;

	return result;
}

/**
 * Divide um objeto de filtro em duas partes: os limites de data (data_venda_inicial
 * e data_venda_final) e o restante dos filtros.
 */
export function splitDateFilters(filter: Record<string, unknown>) {
	const { data_venda_inicial, data_venda_final, ...rest } = filter;
	const datePart: Record<string, unknown> = {};

	if (data_venda_inicial !== undefined) datePart.data_venda_inicial = data_venda_inicial;
	if (data_venda_final !== undefined) datePart.data_venda_final = data_venda_final;

	return { datePart, otherFilters: rest };
}

/**
 * Remove quaisquer chaves `data_venda_*` do objeto de filtro fornecido.
 * Opera em uma cópia superficial, de modo que o original não seja afetado.
 */
export function stripDateFilters(filter: Record<string, unknown>) {
	const result: Record<string, unknown> = { ...filter };

	delete result.data_venda_inicial;
	delete result.data_venda_final;

	return result;
}

/**
 * Determina o próximo reportId e se a nova busca deve reutilizar o
 * relatório anterior (consultar) com base nos períodos de vendas.
 */
export function resolveReportId(
	prevReportId: string | null,
	prevSales?: [string, string],
	currentSales?: [string, string],
) {
	let nextReportId = prevReportId;
	let consult = false;

	if (!prevReportId || !currentSales) nextReportId = generateRandomId();
	else if (prevSales?.[0] === currentSales[0] && prevSales?.[1] === currentSales[1]) consult = true;
	else nextReportId = generateRandomId();

	return { nextReportId, consult };
}
